import { useMemo } from "react";
import {
  SelectCellFormatter,
  SelectColumn,
  useRowSelection,
} from "react-data-grid";
import { commonColumnProps } from "constants/TableConstants";
import { useDownloadWithProgress } from "hooks/useDownloadWithProgress";
import { getDownloadFilenameForDocument } from "utils/document-utils";
import { useConstants } from "utils/hooks";
import { ACCEPTED_PAY_HISTORY_EXTENSIONS } from "pages/future-medical/shared/medical-history/pay-history/ExtractPayHistoryModal";
import ServiceSelector from "../shared/ServiceSelector";
import PreviewButton from "../preview/PreviewButton";
import { FilenameCell } from "./cells/FilenameCell";
import { CmsCheckboxCell } from "./cells/CmsCheckboxCell";
import { DatePickerCell } from "./cells/DatePickerCell";
import type { DocumentType } from "../DocumentTypes";
import type {
  DocumentTableDataType,
  DocumentTableProps,
} from "../DocumentTableTypes";
import { isSelectableForMetadoc } from "../utils/metadocSelection";
import { formatDate } from "../utils/formatters";
import { formatFileSize } from "../DocumentConstants";

export const isParseablePayHistoryDocument = (doc: DocumentType) => {
  const filename = (doc.original_filename || "").toLowerCase();
  return ACCEPTED_PAY_HISTORY_EXTENSIONS.some((ext) => filename.endsWith(ext));
};

type DefaultColumnsProps = {
  hasArScreeningDocuments: boolean;
  hasParseableDocuments?: boolean;
  metaDoc?: boolean;
  selectable?: boolean;
  onDelete: (doc: DocumentType) => void;
  handleToggleCMS: (doc: DocumentType) => void;
  handleChangeDocumentType: (doc: DocumentType, newType: string) => void;
  handleChangeDocumentDate: (doc: DocumentType, date: Date | null) => void;
  handleChangeDocumentService: (
    doc: DocumentType,
    serviceId: number | null,
    lienId?: number | null,
  ) => void;
  updatingDocIds: number[];
  fileServices: NonNullable<DocumentTableProps["fileServices"]>;
  datePickerRefs: React.MutableRefObject<
    Map<number, { setOpen?: (open: boolean) => void } | null>
  >;
  setRows: React.Dispatch<React.SetStateAction<DocumentTableDataType[]>>;
  fileId?: number;
  isAllEligibleMetadocSelected?: boolean;
  hasEligibleMetadocDocuments?: boolean;
  onToggleSelectAllEligibleMetadoc?: (checked: boolean) => void;
  onExtractPayHistory?: (doc: DocumentType) => void;
};

export const useDefaultColumns = ({
  hasArScreeningDocuments,
  hasParseableDocuments = false,
  metaDoc = false,
  selectable = false,
  onDelete,
  handleToggleCMS,
  handleChangeDocumentType,
  handleChangeDocumentDate,
  handleChangeDocumentService,
  updatingDocIds,
  fileServices,
  datePickerRefs,
  setRows,
  isAllEligibleMetadocSelected = false,
  hasEligibleMetadocDocuments = false,
  onToggleSelectAllEligibleMetadoc,
  onExtractPayHistory,
}: DefaultColumnsProps) => {
  const { constants } = useConstants();
  const { startDownload } = useDownloadWithProgress();

  const getDocumentTypeLabel = (type: string) => {
    return (
      constants?.DOCUMENT_TYPES?.find(
        (t: { value: string | null; label: string }) => t.value === type,
      )?.label || type
    );
  };

  const columns = useMemo(() => {
    type SelectCellProps = Parameters<typeof SelectColumn.renderCell>[0] & {
      row: DocumentTableDataType;
    };

    const MetaDocSelectFormatter = ({ row, tabIndex }: SelectCellProps) => {
      const [isRowSelected, onRowSelectionChange] =
        useRowSelection<DocumentTableDataType>();
      const fallbackName = row.original_filename || `document ${row.id}`;
      const isSelectable = isSelectableForMetadoc(row.document);
      const disabledReason =
        row.document.extension?.toLowerCase() !== ".pdf"
          ? "Only PDF documents can be selected for MetaDoc processing."
          : "AI summary and AI sorted/classified documents cannot be selected for MetaDoc processing.";

      return (
        <div
          style={!isSelectable ? { opacity: 0.45, cursor: "not-allowed" } : {}}
          title={!isSelectable ? disabledReason : undefined}
        >
          <SelectCellFormatter
            aria-label={`Select document ${fallbackName}`}
            tabIndex={tabIndex}
            value={isRowSelected}
            disabled={!isSelectable}
            onChange={(checked, isShiftClick) => {
              if (!isSelectable) {
                return;
              }
              onRowSelectionChange({
                type: "ROW",
                row,
                checked,
                isShiftClick,
              });
            }}
          />
        </div>
      );
    };

    const metaDocSelectColumn = {
      ...SelectColumn,
      renderHeaderCell: (props: { tabIndex: number }) => {
        if (!onToggleSelectAllEligibleMetadoc) {
          return null;
        }

        return (
          <SelectCellFormatter
            aria-label="Select all eligible documents"
            tabIndex={props.tabIndex}
            value={isAllEligibleMetadocSelected}
            disabled={!hasEligibleMetadocDocuments}
            onChange={(checked) => onToggleSelectAllEligibleMetadoc(checked)}
          />
        );
      },
      renderCell: (props: SelectCellProps) => {
        return <MetaDocSelectFormatter {...props} />;
      },
    };

    const documentColumns = [
      ...(metaDoc ? [metaDocSelectColumn] : selectable ? [SelectColumn] : []),
      {
        key: "original_filename",
        name: "Document Name",
        ...commonColumnProps,
        minWidth: 400,
        renderCell: ({ row }: { row: DocumentTableDataType }) => {
          return <FilenameCell row={row} />;
        },
      },
      {
        key: "actions",
        name: "Actions",
        ...commonColumnProps,
        width: hasParseableDocuments ? "160px" : "125px",
        minWidth: hasParseableDocuments ? 160 : 125,
        sortable: false,
        renderCell: (props: { row: DocumentTableDataType }) => {
          const doc = props.row.document;
          const isParseablePayHistory = isParseablePayHistoryDocument(doc);

          // source document actions
          return (
            <div
              className="flex no-pd action-buttons"
              style={{
                overflowX: "auto",
                maxWidth: "100%",
                gap: "4px",
                alignItems: "center",
              }}
            >
              <PreviewButton document={doc} tooltipId="preview-tooltip-main" />
              {isParseablePayHistory && onExtractPayHistory && (
                <div
                  className="material-icons edit action-icon"
                  data-tooltip-id="parse-tooltip-main"
                  data-tooltip-content="Parse"
                  data-tooltip-place="top"
                  style={{ color: "#6f42c1" }}
                  onClick={(e) => {
                    e.preventDefault();
                    onExtractPayHistory(doc);
                  }}
                >
                  analytics
                </div>
              )}
              <div
                className="material-icons edit action-icon"
                data-tooltip-id="download-tooltip-main"
                data-tooltip-content="Download"
                data-tooltip-place="top"
                onClick={(e) => {
                  e.preventDefault();

                  startDownload({
                    url: doc.proxy_url,
                    filename: getDownloadFilenameForDocument(
                      doc.original_filename,
                      doc.extension,
                    ),
                    totalBytes: doc.file_size_in_bytes || undefined,
                    onError: (error) => {
                      console.error("Download error:", error);
                      // error is displayed in the download progress toast
                    },
                  });
                }}
              >
                archive
              </div>
              <div
                className="material-icons delete action-icon"
                data-tooltip-id="delete-tooltip-main"
                data-tooltip-content="Delete"
                data-tooltip-place="top"
                onClick={(e) => {
                  e.preventDefault();
                  onDelete(doc);
                }}
              >
                delete
              </div>
            </div>
          );
        },
      },
      {
        key: "file_size_in_bytes",
        name: "File Size",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => (
          <div>{formatFileSize(row.document.file_size_in_bytes) || "-"}</div>
        ),
      },
      {
        key: "page_count",
        name: "Page Count",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => (
          <div>{row.page_count ?? "-"}</div>
        ),
      },
      {
        key: "created",
        name: "Uploaded At",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => (
          <div>{formatDate(row.created)}</div>
        ),
      },
      {
        key: "document_date",
        name: "Document Date",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => {
          return (
            <DatePickerCell
              row={row}
              onChange={handleChangeDocumentDate}
              onLocalUpdate={(rowId, date) => {
                const iso = date ? date.toISOString() : null;
                setRows((rows) =>
                  rows.map((r) =>
                    r.id === rowId
                      ? {
                          ...r,
                          document: { ...r.document, document_date: iso },
                        }
                      : r,
                  ),
                );
              }}
              datePickerRefs={datePickerRefs}
            />
          );
        },
      },
      {
        key: "creator",
        name: "Uploaded By",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => (
          <div>{row.document.creator_name || ""}</div>
        ),
      },
      {
        key: "original_filename_as_uploaded",
        name: "Original Filename",
        ...commonColumnProps,
        minWidth: 220,
        renderCell: ({ row }: { row: DocumentTableDataType }) => {
          const originalFilename =
            row.document.original_filename_as_uploaded || "-";
          const shouldShowTooltip =
            originalFilename !== "-" && originalFilename.length > 50;

          return (
            <div
              {...(shouldShowTooltip && {
                "data-tooltip-id": "original-filename-tooltip",
                "data-tooltip-content": originalFilename,
                "data-tooltip-place": "top",
              })}
            >
              {originalFilename}
            </div>
          );
        },
      },
      {
        key: "type",
        name: "Document Type",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => {
          return (
            <>
              <select
                className="document-type-select"
                value={row.type}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleChangeDocumentType(row.document, e.target.value);
                }}
              >
                {constants?.DOCUMENT_TYPES?.slice()
                  .filter((type) => type.value !== null)
                  .sort((a, b) => {
                    const labelA = getDocumentTypeLabel(a.value || "");
                    const labelB = getDocumentTypeLabel(b.value || "");

                    // pin "Unknown" to the top
                    if (labelA === "Unknown") return -1;
                    if (labelB === "Unknown") return 1;

                    return labelA.localeCompare(labelB);
                  })
                  .map((type) => (
                    <option key={type.value} value={type.value || ""}>
                      {getDocumentTypeLabel(type.value || "")}
                    </option>
                  ))}
              </select>
            </>
          );
        },
      },
      {
        key: "service_type",
        name: "Service",
        ...commonColumnProps,
        minWidth: 220,
        sortable: false,
        renderCell: ({ row }: { row: DocumentTableDataType }) => {
          return (
            <ServiceSelector
              document={row.document}
              fileServices={fileServices}
              isUpdating={updatingDocIds.includes(row.id)}
              onServiceChange={handleChangeDocumentService}
            />
          );
        },
      },
      {
        key: "last_ocr_run_at",
        name: "OCR Run At",
        renderCell: ({ row }: { row: DocumentTableDataType }) => (
          <div className="flex jc-se">
            {row.last_ocr_run_at ? formatDate(row.last_ocr_run_at) : "-"}
          </div>
        ),
        resizeable: true,
      },
      {
        key: "antivirus_scan_status_verbose",
        name: "Virus Scan",
        resizeable: true,
        sortable: false,
      },
    ];

    if (hasArScreeningDocuments) {
      documentColumns.splice(4, 0, {
        key: "ar_screening_document_type_display",
        name: "AR Document Type",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => {
          return <div>{row.ar_screening_document_type_display || "-"}</div>;
        },
      });
    }

    // find the index of the "Service" column and insert CMS right after it
    const serviceTypeIndex = documentColumns.findIndex(
      (col) => col.key === "service_type",
    );
    if (serviceTypeIndex !== -1) {
      documentColumns.splice(serviceTypeIndex + 1, 0, {
        key: "is_cms_submission_document",
        name: "CMS?",
        ...commonColumnProps,
        width: "85px",
        renderCell: ({ row }: { row: DocumentTableDataType }) => {
          return (
            <CmsCheckboxCell
              row={row}
              onChange={handleToggleCMS}
              isUpdating={updatingDocIds.includes(row.id)}
            />
          );
        },
      });
    }

    return documentColumns;
  }, [
    hasArScreeningDocuments,
    hasParseableDocuments,
    metaDoc,
    selectable,
    constants,
    onDelete,
    startDownload,
    handleChangeDocumentDate,
    handleChangeDocumentType,
    handleChangeDocumentService,
    handleToggleCMS,
    updatingDocIds,
    fileServices,
    datePickerRefs,
    setRows,
  ]);

  return columns;
};
